import logger from '@redbubble/next-logger';
import { z } from 'zod';

const translatedStringSchema = z.object({
  en: z.string().nullable().optional(),
  fr: z.string().nullable().optional(),
  de: z.string().nullable().optional(),
  es: z.string().nullable().optional(),
});

const translatedStringsSchema = z.object({
  en: z.array(z.string()).nullable().optional(),
  fr: z.array(z.string()).nullable().optional(),
  de: z.array(z.string()).nullable().optional(),
  es: z.array(z.string()).nullable().optional(),
});

const blankItemOptionValueSchema = z.object({
  key: z.string(),
  value: z.string(),
});

const blankItemOptionsSchema = z.object({
  values: z.array(blankItemOptionValueSchema),
  canonical: z.string(),
  equivalentCanonical: z.string(),
});

const blankItemSchema = z.object({
  id: z.string(),
  blankItemId: z.string(),
  lifecycleStage: z.string(),
  active: z.boolean(),
  options: blankItemOptionsSchema,
});

const colorSchema = z.object({
  kind: z.string(),
  hexColor: z.string().optional(),
  colorName: z.string().optional(),
  texture: z.string().nullable().optional(),
});

const productOptionValueConfigSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('body-sleeve-color'),
    bodyColor: colorSchema,
    sleeveColor: colorSchema,
    pickerColor: colorSchema,
    legacySplitColorName: z.string(),
  }),
  z.object({
    kind: z.literal('case-type'),
    caseModel: z.string(),
    caseType: z.string(),
  }),
  z.object({
    kind: z.literal('generic-size'),
    size: z.string(),
  }),
  z.object({
    kind: z.literal('greeting-card-backing'),
    backing: z.string(),
  }),
  z.object({
    kind: z.literal('laptop-type'),
    laptopType: z.string(),
  }),
  z.object({
    kind: z.literal('paper-type'),
    paperType: z.string(),
  }),
  z.object({
    kind: z.literal('pillow-type'),
    pillowType: z.string(),
  }),
  z.object({
    kind: z.literal('print-location'),
    printLocation: z.string(),
  }),
  z.object({
    kind: z.literal('single-color'),
    color: colorSchema,
  }),
  z.object({
    kind: z.literal('wall-art-border'),
    border: z.string(),
  }),
  z.object({
    kind: z.literal('wall-art-finish'),
    finish: z.string(),
  }),
  z.object({
    kind: z.literal('wall-art-frame-style'),
    frameStyle: z.string(),
  }),
]);

const productOptionValueSchema = z.object({
  kind: z.string(),
  value: z.string(),
  equivalentValue: z.string(),
  publicName: translatedStringSchema,
  publicDisplayOrder: z.number(),
  config: productOptionValueConfigSchema.nullable().optional(),
});

const frontendControlSchema = z.union([
  z.literal('color'),
  z.literal('radio-selector'),
  z.literal('select'),
  z.literal('generic'),
  z.literal('size'),
]);

type OptionType = z.infer<typeof frontendControlSchema>;

export const FrontendControl = {
  Color: 'color',
  RadioSelector: 'radio-selector',
  Select: 'select',
  Generic: 'generic',
  Size: 'size',
} as const satisfies Record<string, OptionType>;

const productOptionSchema = z.object({
  key: z.string(),
  validKinds: z.array(z.string()),
  frontendControl: frontendControlSchema.catch((err) => {
    const message = `Failed to parse frontendControl value: ${err.value}`;
    logger.warn({ err, message, context: 'zodProductOptionSchema' }, message);
    return 'generic';
  }),
  preselect: z.boolean(),
  publicName: translatedStringSchema,
  publicDisplayOrder: z.number(),
  values: z.array(productOptionValueSchema),
});

const iaCodesSchema = z.object({
  tagCloud: z.string().nullable().optional(),
});

const trackingSchema = z.object({
  gaCode: z.string().nullable().optional(),
});

const shopSchema = z.object({
  showCardQuickAddToCart: z.boolean(),
  seoCategory: z.string().nullable().optional(),
});

const productTypeSchema = z.object({
  id: z.string(),
  productTypeId: z.string(),
  productFamilyId: z.string().nullable().optional(),
  defaultBlankItemId: z.string(),
  productFamilyOptionSelections: blankItemOptionsSchema.nullable().optional(),
  productTypeUrlId: z.string(),
  lifecycleStage: z.string(),
  copy: z
    .object({
      publicName: translatedStringSchema,
      features: translatedStringsSchema,
    })
    .nullable()
    .optional(),
  blankItems: z.array(blankItemSchema),
  options: z.array(productOptionSchema),
  iaCodes: iaCodesSchema,
  marketingProductType: z.string().nullable().optional(),
  tracking: trackingSchema,
  shop: shopSchema,
});

const productFamilyOptionValueConfigSchema = z.object({
  kind: z.literal('type'),
});

const productFamilyOptionValueSchema = z.object({
  value: z.string(),
  publicDisplayOrder: z.number(),
  publicName: translatedStringSchema,
  equivalentValue: z.string(),
  kind: z.string(),
  active: z.boolean(),
  config: productFamilyOptionValueConfigSchema.nullable().optional(),
});

const productFamilyOptionSchema = z.object({
  key: z.string(),
  publicDisplayOrder: z.number(),
  required: z.boolean(),
  values: z.array(productFamilyOptionValueSchema),
  publicName: translatedStringSchema,
  validKinds: z.array(z.string()),
  frontendControl: frontendControlSchema.catch((err) => {
    const message = `Failed to parse frontendControl value: ${err.value}`;
    logger.warn(
      { err, message, context: 'zodProductFamilyOptionSchema' },
      message
    );
    return 'generic';
  }),
  preselect: z.boolean(),
});

const _productFamilyOptionOrder = z.object({
  kind: z.string(),
  key: z.string(),
  publicDisplayOrder: z.number(),
});

const productFamilySchema = z.object({
  productFamilyId: z.string(),
  productTypeIds: z.array(z.string()),
  lifecycleStage: z.string(),
  defaultProductTypeId: z.string(),
  warnOnOptionFallback: z.boolean(),
  publicName: translatedStringSchema,
  publicNamePlural: translatedStringSchema,
  options: z.array(productFamilyOptionSchema),
  optionsOrder: z.array(
    z.object({
      kind: z.enum(['product-option', 'product-family-option']),
      key: z.string(),
      publicDisplayOrder: z.number(),
    })
  ),
});

const blankItemReferralSchema = z.object({
  sourceBlankItemId: z.string(),
  targetBlankItemId: z.string(),
});

const productTypeReferralConfiguredLandingPage = z.object({
  kind: z.literal('configured-landing-page'),
  configuredLandingPage: z.string(),
});
const productTypeReferralProductType = z.object({
  kind: z.literal('product-type'),
  targetProductTypeId: z.string(),
});
const productTypeReferralSearchResult = z.object({
  kind: z.literal('search-result'),
  query: z.string(),
});
export const productTypeReferral = z.discriminatedUnion('kind', [
  productTypeReferralConfiguredLandingPage,
  productTypeReferralProductType,
  productTypeReferralSearchResult,
]);

const productTypeForReferralSchema = z.object({
  productTypeId: z.string(),
  productTypeUrlId: z.string(),
  lifecycleStage: z.string(),
  referral: productTypeReferral.nullable().catch((err) => {
    const message = 'Unhandled ProductTypeReferral kind. Ignoring.';
    logger.error(
      {
        err,
        context: 'getProductTypesAndFamiliesSchema/productTypeReferral',
        message,
      },
      message
    );
    return null;
  }),
  blankItems: z.array(
    z.object({
      blankItemId: z.string(),
      lifecycleStage: z.string(),
    })
  ),
});

export const getProductTypesAndFamiliesSchema = z.object({
  productTypes: z.array(productTypeSchema.nullable()),
  productFamilies: z.array(productFamilySchema.nullable()).optional(),
  productTypesForReferral: z.array(productTypeForReferralSchema),
  blankItemReferrals: z.array(blankItemReferralSchema.nullable()).optional(),
});

export type ZodProductType = z.infer<typeof productTypeSchema>;
export type ZodProductFamily = z.infer<typeof productFamilySchema>;
export type ZodProductTypeForReferral = z.infer<
  typeof productTypeForReferralSchema
>;
export type ZodProductTypeReferral = z.infer<typeof productTypeReferral>;
export type ZodBlankItem = z.infer<typeof blankItemSchema>;
export type ZodTranslatedString = z.infer<typeof translatedStringSchema>;
